import { forwardRef, type CSSProperties, type MouseEvent, type ReactNode } from "react";
import { cn } from "@/lib/utils";
import { buttonVariants } from "@/components/ui/button";
import { getAffiliateLink } from "@/lib/affiliate-registry";
import { type AffiliateId } from "@/config/affiliateLinks";
import { ExternalLink } from "lucide-react";
import { trackAdClick, type AdPlacement } from "@/lib/ad-tracking";

interface AffiliateLinkProps {
  affiliateId: AffiliateId | string;
  children?: ReactNode;
  className?: string;
  variant?: "text" | "button";
  showIcon?: boolean;
  style?: CSSProperties;
  placement?: AdPlacement | string;
  onClick?: (e: MouseEvent<HTMLAnchorElement>) => void;
}

export const AffiliateLink = forwardRef<HTMLAnchorElement, AffiliateLinkProps>(
  ({ affiliateId, children, className, variant = "text", showIcon = false, style, placement = "unknown", onClick }, ref) => {
    const link = getAffiliateLink(affiliateId);
    if (!link) {
      if (typeof window !== "undefined" && import.meta.env.DEV) {
        console.warn(`[AffiliateLink] Ukendt affiliateId: ${affiliateId}`);
      }
      return null;
    }

    const label = children ?? link.defaultCta;

    const classes =
      variant === "button"
        ? cn(buttonVariants({ variant: "accent" }), className)
        : cn("text-primary underline underline-offset-2 hover:text-primary/80", className);

    const handleClick = (e: MouseEvent<HTMLAnchorElement>) => {
      trackAdClick(link.id, placement);
      onClick?.(e);
    };

    return (
      <a
        ref={ref}
        href={link.url}
        target="_blank"
        rel="sponsored nofollow noopener"
        data-affiliate-id={link.id}
        data-affiliate-network={link.network}
        data-affiliate-placement={placement}
        className={classes}
        style={style}
        onClick={handleClick}
      >
        {label}
        {showIcon && <ExternalLink className="ml-1 h-3.5 w-3.5" aria-hidden="true" />}
        <span className="sr-only"> (annoncelink, åbner i ny fane)</span>
      </a>
    );
  },
);
AffiliateLink.displayName = "AffiliateLink";
